import { Pencil, Trash2, Search, RotateCcw } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  Row,
  Table
} from 'react-bootstrap'
import { useDebounce } from 'use-debounce'
import Swal from 'sweetalert2'
import Loader from '../../../components/atoms/Loader'
import RowEmpty from '../../../components/atoms/RowEmpty'
import { ActiveInActive } from '../../../components/atoms/StatusLabel'
import TableAction from '../../../components/atoms/Table/TableAction'
import TablePagination from '../../../components/atoms/Table/TablePagination'
import ModalAddBusinessUnit from '../../../components/modal/businessunit/ModalAddBusinessUnit'
import ModalUpdateBusinessUnit from '../../../components/modal/businessunit/ModalUpdateBusinessUnit'
import api from '../../../config/AxiosInstance'
import { convertDateWithTime } from '../../../helpers/constants'
import '../../../styles/style.css'
import * as Common from '../../../utils/Common'

const initialMeta = {
  per_page: 10,
  current_page: 1,
  total_row: 0,
  total_page: 0
}

const headerTitleStyle = {
  color: 'var(--primary-orange)',
  fontSize: '24px',
  fontWeight: '600'
}

const tableHeaderStyle = {
  fontSize: '14px',
  fontWeight: '600',
  color: '#333333',
  letterSpacing: '0.5px',
  backgroundColor: '#f8f9fa',
  borderBottom: '2px solid #e9ecef'
}

const cardFilterStyle = {
  border: 'none',
  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  borderRadius: '12px',
  padding: '1.5rem',
  marginBottom: '1.5rem',
  backgroundColor: '#fff'
}

const labelStyle = {
  fontSize: '0.75rem',
  fontWeight: '700',
  color: '#333333',
  textTransform: 'uppercase',
  marginBottom: '0.5rem',
  letterSpacing: '0.5px'
}

const FormControlStyle = {
  height: '45px',
  borderRadius: '10px',
  border: '1px solid #e9ecef',
  backgroundColor: '#ffffff',
  fontSize: '0.9rem'
}

const MasterBusinessUnit = () => {
  const [modalAddShow, setModalAddShow] = useState(false)
  const [modalUpdateShow, setModalUpdateShow] = useState(false)
  const [editId, setEditId] = useState(null)
  const [buList, setBuList] = useState([])
  const [meta, setMeta] = useState(initialMeta)
  const [loading, setLoading] = useState(false)
  const [query, setQuery] = useState('')
  const [debouncedQuery] = useDebounce(query, 500)

  const { per_page, current_page } = meta
  const prevQuery = useRef({ q: debouncedQuery })
  const abortControllerRef = useRef(null)

  const loadData = async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    abortControllerRef.current = new AbortController()
    const signal = abortControllerRef.current.signal

    setLoading(true)
    try {
      const { data } = await api.get('master/business-units', {
        signal,
        params: {
          per_page,
          page: current_page,
          q: debouncedQuery || ''
        }
      })
      setMeta(data.metadata)
      setBuList(data.data.rows || data.data)
    } catch (err) {
      if (
        err.name !== 'CanceledError' &&
        err.message !== 'canceled' &&
        err.code !== 'ERR_CANCELED'
      ) {
        const errMsg =
          err?.response?.data?.errors?.message || 'An error occurred'
        Common.showPopUpErrorMessage(errMsg)
      }
    } finally {
      if (!signal.aborted) {
        setLoading(false)
      }
    }
  }

  useEffect(() => {
    const queryChanged = prevQuery.current.q !== debouncedQuery
    if (queryChanged) {
      prevQuery.current = { q: debouncedQuery }
      if (current_page !== 1) {
        setMeta((prev) => ({ ...prev, current_page: 1 }))
        return
      }
    }

    loadData()

    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery, per_page, current_page])

  const handleOnChangeMeta = (e) => {
    setMeta((prevMeta) => ({
      ...prevMeta,
      per_page: parseInt(e.target.value, 10),
      current_page: 1
    }))
    window.scrollTo(0, 0)
  }

  const handlePageChange = (data) => {
    setMeta((prevMeta) => ({
      ...prevMeta,
      current_page: data.selected + 1
    }))
    window.scrollTo(0, 0)
  }

  const handleEdit = (id) => {
    setEditId(id)
    setModalUpdateShow(true)
  }

  const handleToggleStatus = (item) => {
    const actionText = item.status ? 'deactivate' : 'activate'
    Swal.fire({
      title: 'Confirmation',
      text: `Are you sure you want to ${actionText} Business Unit "${item.name}"?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#ff7a00',
      reverseButtons: true
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.patch(`master/business-units/${item.id}/toggle-status`)
          Common.showPopUpSuccessMessage(
            `Business Unit ${actionText}d successfully`
          )
          loadData()
        } catch (err) {
          const errMsg =
            err?.response?.data?.errors?.message || 'An error occurred'
          Common.showPopUpErrorMessage(errMsg)
        }
      }
    })
  }

  const handleDelete = (item) => {
    Swal.fire({
      title: 'Confirmation',
      text: `Are you sure you want to delete Business Unit "${item.name}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, Delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#d33',
      reverseButtons: true
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.delete(`master/business-units/${item.id}`)
          Common.showPopUpSuccessMessage('Business Unit deleted successfully')
          loadData()
        } catch (err) {
          const errMsg =
            err?.response?.data?.errors?.message || 'An error occurred'
          Common.showPopUpErrorMessage(errMsg)
        }
      }
    })
  }

  const handleReset = () => {
    setQuery('')
    setMeta((prev) => ({ ...prev, current_page: 1 }))
  }

  const refreshData = () => loadData()

  return (
    <Container fluid className="px-4">
      <Row className="mb-4 align-items-center">
        <Col>
          <h3 style={headerTitleStyle}>Business Unit</h3>
        </Col>
        <Col className="text-end d-flex align-items-center justify-content-end gap-2">
          <Button
            variant="light"
            className="text-secondary fw-bold rounded px-3 border"
            onClick={handleReset}
            style={{ fontSize: '0.85rem', height: '40px' }}
          >
            <RotateCcw size={16} className="me-2" />
            Reset Filters
          </Button>
          <Button
            className="btn btn-primary-orange shadow-sm rounded px-4"
            onClick={() => setModalAddShow(true)}
            style={{ height: '40px' }}
          >
            Add Business Unit
          </Button>
        </Col>
      </Row>

      <Card style={cardFilterStyle}>
        <Row className="g-3">
          <Col md={4}>
            <Form.Group>
              <Form.Label style={labelStyle}>Search</Form.Label>
              <InputGroup>
                <InputGroup.Text
                  style={{
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #e9ecef',
                    borderRight: 'none',
                    borderTopLeftRadius: '10px',
                    borderBottomLeftRadius: '10px'
                  }}
                >
                  <Search size={18} className="text-muted" />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  style={{
                    ...FormControlStyle,
                    borderLeft: 'none',
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0
                  }}
                  placeholder="Search Business Unit..."
                />
              </InputGroup>
            </Form.Group>
          </Col>
        </Row>
      </Card>

      <Row>
        <Col md={12}>
          <Table
            hover
            responsive
            className="shadow-sm mb-5 bg-white"
            style={{
              borderRadius: '12px',
              overflow: 'hidden',
              fontSize: '0.9rem'
            }}
          >
            <thead style={{ backgroundColor: '#f8f9fa' }}>
              <tr>
                <th className="p-2 align-middle" style={tableHeaderStyle}>
                  Name
                </th>
                <th
                  className="p-2 align-middle text-end"
                  style={tableHeaderStyle}
                >
                  Status
                </th>
                <th
                  className="p-2 align-middle text-end"
                  style={tableHeaderStyle}
                >
                  Created At
                </th>
                <th
                  className="p-2 align-middle text-end"
                  style={tableHeaderStyle}
                >
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={4} className="text-center p-5">
                    <Loader />
                  </td>
                </tr>
              )}
              {!loading && !buList.length && (
                <tr>
                  <td colSpan={4}>
                    <RowEmpty colSpan={4} />
                  </td>
                </tr>
              )}
              {!loading &&
                buList.map((data, i) => (
                  <tr
                    key={data.id || i}
                    style={{ borderBottom: '1px solid #f0f0f0' }}
                  >
                    <td className="p-2 align-middle fw-semibold text-primary">
                      {data?.name}
                    </td>
                    <td className="p-2 align-middle text-end">
                      <span
                        className="cursor-pointer"
                        onClick={() => handleToggleStatus(data)}
                        title="Click to toggle status"
                      >
                        <ActiveInActive
                          text={data.status ? 'active' : 'inactive'}
                        />
                      </span>
                    </td>
                    <td className="p-2 align-middle text-end text-muted">
                      {convertDateWithTime(data?.createdAt)}
                    </td>
                    <td className="p-2 align-middle text-end">
                      <TableAction>
                        <button
                          className="btn btn-light shadow-sm rounded d-flex align-items-center justify-content-center p-0 me-1"
                          style={{ width: '32px', height: '32px' }}
                          onClick={() => handleEdit(data.id)}
                          title="Edit"
                        >
                          <Pencil size={13} />
                        </button>
                        <button
                          className="btn btn-light text-danger shadow-sm rounded d-flex align-items-center justify-content-center p-0"
                          style={{ width: '32px', height: '32px' }}
                          onClick={() => handleDelete(data)}
                          title="Delete"
                        >
                          <Trash2 size={13} />
                        </button>
                      </TableAction>
                    </td>
                  </tr>
                ))}
            </tbody>
            {buList.length > 0 && (
              <tfoot>
                <tr>
                  <td colSpan={4} className="p-3 border-top-0">
                    <TablePagination
                      meta={meta}
                      handlePageChange={handlePageChange}
                      handleOnChangeMeta={handleOnChangeMeta}
                    />
                  </td>
                </tr>
              </tfoot>
            )}
          </Table>
        </Col>
      </Row>

      <ModalAddBusinessUnit
        show={modalAddShow}
        onHide={() => setModalAddShow(false)}
        refreshData={refreshData}
      />

      {modalUpdateShow && (
        <ModalUpdateBusinessUnit
          show={modalUpdateShow}
          onHide={() => setModalUpdateShow(false)}
          id={editId}
          refreshData={refreshData}
        />
      )}
    </Container>
  )
}

export default MasterBusinessUnit
